import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';
import { moneyTight } from '../utils/region';

// ── "We received your VAT / WHT payment" composer (TPL-045, owner 2026-08-28) ───────────────────
// VAT and WHT are collected to be remitted, so no receipt is ever issued — before this the client
// got nothing back and the match panel opened an email that did not exist. Opened straight after
// a VAT/WHT match (bank or petty cash) and re-openable from the matched row.
// Editing the Description re-renders the draft, so the subject and the body can never disagree.

const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

function Chips({ value, onChange, ph }: { value: string[]; onChange: (v: string[]) => void; ph: string }) {
  const [q, setQ] = useState('');
  const add = (raw: string) => { const v = raw.trim().toLowerCase(); if (v && validEmail(v) && !value.includes(v)) onChange([...value, v]); setQ(''); };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)' }}>
      {value.map(e => (
        <span key={e} style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 8, background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {e}<span onClick={() => onChange(value.filter(x => x !== e))} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>
        </span>
      ))}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={ph}
        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && q.trim()) { e.preventDefault(); add(q); } }}
        onBlur={() => q.trim() && add(q)}
        style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)' }} />
    </div>
  );
}

export default function TaxPaidEmailComposer({ source, txnId, onClose, onSent }: {
  source: 'bank' | 'petty'; txnId: string; onClose: () => void; onSent?: () => void;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(''); const [to, setTo] = useState<string[]>([]); const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState(''); const [initialBody, setInitialBody] = useState('');
  const [desc, setDesc] = useState(''); const [amount, setAmount] = useState(0);
  const [warning, setWarning] = useState(''); const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false); const [done, setDone] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const prep = (description?: string) => api.prepareTaxPaidEmail(source, txnId, description).then((d: any) => {
    setFrom(d.from || ''); setTo(d.to || []); setCc(d.cc || []);
    setSubject(d.subject || ''); setInitialBody(d.bodyHtml || '');
    setDesc(d.description || ''); setAmount(d.amount || 0); setWarning(d.warning || '');
    if (bodyRef.current) bodyRef.current.innerHTML = d.bodyHtml || '';
  });

  useEffect(() => { prep().catch((e: any) => setErr(e?.message || 'Failed to prepare email')).finally(() => setLoading(false)); }, []); // eslint-disable-line

  const send = async () => {
    if (!to.length) { setErr(t('iv_email_need')); return; }
    setBusy(true); setErr('');
    try {
      const body = bodyRef.current ? bodyRef.current.innerHTML : initialBody;
      await api.sendTaxPaidEmail({ source, id: txnId, from, to, cc, subject, bodyHtml: body });
      setDone(true); onSent?.(); setTimeout(onClose, 1400);
    } catch (e: any) { setErr(e?.message || 'Failed to send'); } finally { setBusy(false); }
  };

  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };
  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10004, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && !busy && onClose()}>
      <div style={{ width: 680, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('tp_email_title')}</div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0F766E', background: '#ECFDF5', border: '1px solid #99F6E4', borderRadius: 999, padding: '3px 11px' }}>
            {moneyTight(amount)}
          </div>
        </div>
        {loading ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div> : done ? (
          <div style={{ fontSize: 13.5, color: '#15803D', fontWeight: 700, padding: '10px 0' }}><i className="ti ti-circle-check" style={{ marginRight: 6 }} />{t('iv_email_sent')}</div>
        ) : (<>
          {warning && <div style={{ fontSize: 12.5, color: '#92400E', background: '#FEF3C7', borderRadius: 9, padding: '8px 11px', marginBottom: 12 }}><i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />{warning}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            <div><label style={lbl}>{t('bie_from')}</label><input value={from} onChange={e => setFrom(e.target.value)} style={inp} /></div>
            <div><label style={lbl}>{t('bie_to')}</label><Chips value={to} onChange={setTo} ph={t('bie_email_ph')} /></div>
            <div><label style={lbl}>{t('bie_cc')}</label><Chips value={cc} onChange={setCc} ph={t('bie_email_ph')} /></div>
            <div>
              <label style={lbl}>{t('tp_desc')}</label>
              <input value={desc} onChange={e => setDesc(e.target.value)}
                onBlur={() => { if (desc.trim()) prep(desc.trim()).catch(() => {}); }}
                style={inp} />
              <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('tp_desc_hint')}</div>
            </div>
            <div><label style={lbl}>{t('bie_subject')}</label><input value={subject} onChange={e => setSubject(e.target.value)} style={inp} /></div>
            <div>
              <label style={lbl}>{t('bie_body')}</label>
              <div ref={bodyRef} contentEditable suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: initialBody }}
                style={{ ...inp, minHeight: 220, maxHeight: 340, overflowY: 'auto', background: '#fff', lineHeight: 1.55 }} />
            </div>
          </div>
          {err && <div style={{ fontSize: 12.5, color: '#E24B4A', background: '#FCEBEB', borderRadius: 9, padding: '8px 11px', marginTop: 12 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={onClose} disabled={busy} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
            <button onClick={send} disabled={busy || !to.length} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13, cursor: busy ? 'default' : 'pointer', opacity: busy || !to.length ? 0.6 : 1 }}>
              <i className="ti ti-send" style={{ marginRight: 6 }} />{busy ? '…' : t('bs_send_email')}
            </button>
          </div>
        </>)}
      </div>
    </div>
  );
}
